import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { GraphDataEthnicity, PredictedAt18Qualifier, SingleMyopRiskAt18Result } from '../../backend-response-models/calc-enums.helper';
import { Dataset } from '../../shared/shared-enums/enum-helper';
import { SingleMyopeResults } from '../../backend-response-models/calc-interfaces.helper';
import { TranslatePipe } from '../../languages/translate.pipe';
import { COMMON_IMPORTS } from '../../common-imports';
import { ChartModule } from 'primeng/chart';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { PanelModule } from 'primeng/panel';
import { blackColorTL, greenColorTL, redColorTL, whiteColorTL, yellowColorTL } from '../../helpers/ui.helper';
import { ChipModule } from 'primeng/chip';
import { MyopiaSingleEimRetestComponent } from '../myopia-single-eim-retest/myopia-single-eim-retest.component';

@Component({
  selector: 'app-future-myopia',
  standalone: true,
  imports: [...COMMON_IMPORTS, PanelModule, DropdownModule, ChartModule, ChipModule, MyopiaSingleEimRetestComponent],
  templateUrl: './future-myopia.component.html',
  styleUrl: './future-myopia.component.css',
})
export class FutureMyopiaComponent implements OnInit, OnChanges {
  SingleMyopRiskAt18Result = SingleMyopRiskAt18Result;

  @Input()
  ethnicity?: GraphDataEthnicity;

  @Input({ required: true })
  singleMyopeResults?: SingleMyopeResults;

  @Output()
  ethnicityChanged = new EventEmitter<GraphDataEthnicity>();

  public ethnicityDict: Record<GraphDataEthnicity, string> = {
    [GraphDataEthnicity.WESTERN]: 'WESTERN_DATA_CHOICE',
    [GraphDataEthnicity.EAST_ASIAN]: 'EAST_ASIAN_DATA_CHOICE',
  };
  public dataSets: Dataset[] = [];
  public selectedDataset?: Dataset;

  public releventAge: number = 18;
  public releventSingleMyopRiskAt18?: SingleMyopRiskAt18Result;
  public releventSerPredictedAt18?: number;
  public myopiaPredictionAt18: string = 'NONE';
  public levelOfMyopia: string = 'LOW';
  public levelOfMyopiaColor: string = greenColorTL;
  public levelOfMyopiaTextColor: string = blackColorTL;
  public retestPeriodsMonths: number = 18;
  alec: string | undefined;

  constructor(private readonly translatePipe: TranslatePipe) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this.refreshAll();
  }

  ngOnInit(): void {
    if (!this.ethnicity) {
      this.ethnicity = GraphDataEthnicity.WESTERN;
    }
    this.refreshAll();
  }

  private refreshAll() {
    this.retestPeriodsMonths = this.singleMyopeResults?.retestPeriodMonths ?? 18;
    this.addDataSets();
    this.selectedDataset = this.dataSets.find(d => d.value === (this.ethnicity ?? GraphDataEthnicity.WESTERN));
    this.refreshLevelOfMyopia();
  }

  private addDataSets() {
    this.dataSets = this.singleMyopeResults!.allEthnicities.map(e => {
      const keyForTranslation = this.ethnicityDict[e.graphDataEthnicity];
      return {
        label: keyForTranslation ? this.translatePipe.transform(keyForTranslation) : e.graphDataEthnicity.toString(),
        value: e.graphDataEthnicity,
      };
    });
  }

  onDatasetChanged(event: DropdownChangeEvent) {
    this.ethnicity = event.value.value;
    this.selectedDataset = event.value;
    this.ethnicityChanged.emit(this.ethnicity);
    this.refreshAll();
  }

  refreshLevelOfMyopia() {
    const translationKeysForLevelOfMyopia: Record<string, string> = {
      [SingleMyopRiskAt18Result.LOW]: 'AGE_18_MYOPIA_LOW_PREDICTION',
      [SingleMyopRiskAt18Result.MEDIUM]: 'AGE_18_MYOPIA_MEDIUM_PREDICTION',
      [SingleMyopRiskAt18Result.HIGH]: 'AGE_18_MYOPIA_HIGH_PREDICTION',
    };
    const myopiaLevelColors: Record<string, string> = {
      [SingleMyopRiskAt18Result.LOW]: greenColorTL,
      [SingleMyopRiskAt18Result.MEDIUM]: yellowColorTL,
      [SingleMyopRiskAt18Result.HIGH]: redColorTL,
    };

    const myopiaTextLevelColors: Record<string, string> = {
      [SingleMyopRiskAt18Result.LOW]: whiteColorTL,
      [SingleMyopRiskAt18Result.MEDIUM]: blackColorTL,
      [SingleMyopRiskAt18Result.HIGH]: whiteColorTL,
    };

    const releventData = this.singleMyopeResults!.allEthnicities.find(e => e.graphDataEthnicity === this.ethnicity);
    if (!releventData) {
      return;
    }

    this.releventSingleMyopRiskAt18 = releventData.singleMyopRiskAt18Result;
    this.releventAge = releventData.ageAtLatestVisit;
    this.releventSerPredictedAt18 = releventData.serPredictedAt18;

    this.levelOfMyopia = this.translatePipe.transform(translationKeysForLevelOfMyopia[releventData!.singleMyopRiskAt18Result]);
    this.levelOfMyopiaColor = myopiaLevelColors[releventData!.singleMyopRiskAt18Result];
    this.levelOfMyopiaTextColor = myopiaTextLevelColors[releventData!.singleMyopRiskAt18Result];

    let serPredictedDisplay: string = '--';

    if (releventData.serPredictedAt18) {
      serPredictedDisplay = releventData.serPredictedAt18.toFixed(2).toString();
    }

    this.myopiaPredictionAt18 = serPredictedDisplay + 'D';

    if (releventData.serPredictionAt18Qualifier === PredictedAt18Qualifier.MYOPIC_LESS_THAN_6_YEARS_OLD) {
      this.myopiaPredictionAt18 =
        this.translatePipe.transform('OVER') +
        ' ' +
        this.myopiaPredictionAt18 +
        ' ' +
        this.translatePipe.transform('CAUTION_AGE_OUT_OF_BOUNDS');
    }

    console.log('Level of Myopia: ', this.levelOfMyopiaColor);
  }
}
